import {Button, Menu, MenuItem, Popover, Tooltip} from "@blueprintjs/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {ICellRendererParams} from "@ag-grid-community/core";
import {RowData} from "../types/types";
import {RoutePlanningDriver} from "../../../providers/RoutePlanningProvider";
import styled from "@emotion/styled";

type DriverActionMenuParams = ICellRendererParams<RowData> & {
  selectedDrivers: RoutePlanningDriver[];
  /** DP-3039: whether any active driver outside the run exists (gates Change via "Other drivers"). */
  hasOtherDrivers?: boolean;
  /** DP-3039: fresh route rows — route ownership drives the enable guard, not stale params.data. */
  workingRowDataRef?: React.MutableRefObject<RowData[]>;
  /** DP-3039: open the lifted-out Swap-or-Change dialog (rendered by DriverResultList). */
  onOpenSwapChange: (routeId: string) => void;
  onDeleteRoute: (routeId: string) => void;
  /** DP-3175: show star icon for existing manifest routes when maintaining existing work. */
  showManifestStar?: boolean;
  /** DP-3175: manifest driver IDs left untouched by integrity fallback. */
  untouchedManifestDriverIds?: number[];
};

const UNTOUCHED_TOOLTIP =
  "Route has no changes. ETAs and miles are calculated based on the start time and location from the previous step. Nothing will be applied after clicking Create Routes.";

const DriverActionMenuCellRenderer = (params: DriverActionMenuParams) => {
  const {
    data,
    value,
    selectedDrivers,
    hasOtherDrivers,
    workingRowDataRef,
    onOpenSwapChange,
    onDeleteRoute,
    showManifestStar,
    untouchedManifestDriverIds
  } = params;

  if (!data?.driverId) return <span>{value}</span>;

  // routeId is immutable per row, so it is safe even if ag-grid's params.data is stale.
  const routeId = data.routeId;

  // Read the acted-on route's driver from the FRESH rows (params.data can lag after a swap).
  const rows = workingRowDataRef?.current ?? [];
  const currentRow = rows.find((row) => row.routeId === routeId) ?? data;
  const driverId = currentRow.driverId ?? data.driverId;

  // DP-3175: manifest routes cannot have their driver swapped/changed
  const isManifestRoute = currentRow.manifestDriverId != null;
  const isUntouched = isManifestRoute && (untouchedManifestDriverIds ?? []).includes(currentRow.manifestDriverId!);

  // DP-3039 enable guard: Swap needs another route-owning driver; Change needs an in-run other
  // OR an out-of-run driver (the old `availableDrivers === 0` check wrongly disabled Change with
  // 1 driver in the run). The full option lists are computed by the dialog owner (DriverResultList).
  const swapAvailable =
    !isManifestRoute &&
    rows.some((row) => row.routeId !== routeId && row.driverId != null && row.driverId !== driverId);
  const inRunOthers = selectedDrivers.some((driver) => driver.driverId != null && driver.driverId !== driverId);
  const changeAvailable = !isManifestRoute && (inRunOthers || !!hasOtherDrivers);

  const menuButton = (
    <Button
      variant="minimal"
      size="small"
      icon={<FontAwesomeIcon icon={solid("ellipsis-vertical")} size="lg" color="#6B7280" />}
      disabled={isManifestRoute}
      style={{minHeight: 20, minWidth: 20, padding: 0, position: "relative", top: -1}}
    />
  );

  return (
    <DriverRow>
      {/* Wrapper div stops click from reaching agGroupCellRenderer's expand/collapse handler */}
      <MenuWrapper role="none" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        {isManifestRoute ? (
          <Tooltip content="Cannot modify an existing manifest route" placement="top">
            {menuButton}
          </Tooltip>
        ) : (
          <Popover
            content={
              <Menu>
                <MenuItem
                  text="Swap or Change Driver"
                  icon={<FontAwesomeIcon icon={solid("right-left")} />}
                  disabled={!changeAvailable && !swapAvailable}
                  onClick={() => onOpenSwapChange(routeId)}
                />
                <MenuItem
                  text="Delete Route"
                  icon={<FontAwesomeIcon icon={solid("trash")} />}
                  onClick={() => onDeleteRoute(routeId)}
                />
              </Menu>
            }
            placement="bottom-start"
            minimal
          >
            {menuButton}
          </Popover>
        )}
      </MenuWrapper>
      {isManifestRoute ? (
        <Tooltip
          content={`Manifest #${data.manifestDriverId}`}
          placement="top"
          targetProps={{style: {flex: "0 1 auto", overflow: "hidden", minWidth: 0}}}
        >
          <DriverName>
            <DriverNameTag>{value}</DriverNameTag>
          </DriverName>
        </Tooltip>
      ) : (
        <DriverName>{value}</DriverName>
      )}
      {showManifestStar && data.manifestDriverId != null && (
        <IconWrapper>
          <StarIcon icon={solid("star")} size="sm" color="#D97706" />
        </IconWrapper>
      )}
      {isUntouched && (
        <IconWrapper>
          <Tooltip content={<TooltipContent>{UNTOUCHED_TOOLTIP}</TooltipContent>} placement="top">
            <InfoIcon icon={solid("circle-info")} size="sm" color="#3B82F6" />
          </Tooltip>
        </IconWrapper>
      )}
    </DriverRow>
  );
};

export default DriverActionMenuCellRenderer;

const DriverRow = styled.span`
  display: flex;
  align-items: center;
  height: 100%;
  gap: 4px;
  overflow: hidden;
`;

const MenuWrapper = styled.div`
  flex-shrink: 0;
`;

const DriverName = styled.span`
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DriverNameTag = styled.span`
  border: 1px solid #93c5fd;
  border-radius: 4px;
  padding: 0 5px;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  line-height: 20px;
  height: 22px;
`;

const IconWrapper = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`;

const StarIcon = styled(FontAwesomeIcon)`
  position: relative;
  top: 0px;
`;

const InfoIcon = styled(FontAwesomeIcon)`
  position: relative;
  top: 2px;
`;

const TooltipContent = styled.span`
  display: inline-block;
  max-width: 260px;
`;
